import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { MessageCircle, Download, Share2, ArrowRight } from 'lucide-react';
import { trackEvent } from '@/lib/posthog';
// Card image is generated via Canvas 2D API (no html2canvas needed)

const translations = {
  fr: {
    yourScore: 'Ton Score CV',
    before: 'Avant',
    after: 'Après',
    tipsIdentified: 'conseils d\'amélioration identifiés',
    tipIdentified: 'conseil d\'amélioration identifié',
    glowUp: 'Plus gros glow-up ! 🔥',
    seeTips: 'Voir mes conseils →',
    shareScore: 'Partager mon score',
    whatsapp: 'WhatsApp',
    download: 'Télécharger',
  },
  en: {
    yourScore: 'Your CV Score',
    before: 'Before',
    after: 'After',
    tipsIdentified: 'improvement tips identified',
    tipIdentified: 'improvement tip identified',
    glowUp: 'Biggest glow-up! 🔥',
    seeTips: 'See my tips →',
    shareScore: 'Share my score',
    whatsapp: 'WhatsApp',
    download: 'Download',
  }
};

function getScoreColor(score) {
  if (score >= 9) return '#FFD700';
  if (score >= 7) return '#3DC73E';
  if (score >= 5) return '#FEA501';
  return '#E53E3E';
}

function getShareText(scoreBefore, scoreAfter, language = 'en') {
  const url = 'https://cv.edomatch.com/?utm_source=celebration&utm_medium=share';
  if (language === 'fr') {
    return `🎯 Mon CV est passé de ${scoreBefore}/10 à ${scoreAfter}/10 grâce à EdoMatch !\n\n💡 Vérifie ton score gratuitement ici 👇\n${url}`;
  }
  return `🎯 My CV went from ${scoreBefore}/10 to ${scoreAfter}/10 thanks to EdoMatch!\n\n💡 Check your score for free here 👇\n${url}`;
}

// CSS-only confetti
const CONFETTI_COLORS = ['#FFD700', '#3DC73E', '#FEA501', '#E53E3E', '#5BA946', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'];

function ConfettiPiece({ index }) {
  const color = CONFETTI_COLORS[index % CONFETTI_COLORS.length];
  const left = Math.random() * 100;
  const delay = Math.random() * 3;
  const duration = 2 + Math.random() * 3;
  const size = 6 + Math.random() * 8;
  const rotation = Math.random() * 360;

  return (
    <div
      style={{
        position: 'absolute',
        top: '-20px',
        left: `${left}%`,
        width: `${size}px`,
        height: `${size * 0.6}px`,
        backgroundColor: color,
        borderRadius: '2px',
        transform: `rotate(${rotation}deg)`,
        animation: `confettiFall ${duration}s ease-in ${delay}s forwards`,
        opacity: 0,
      }}
    />
  );
}

function CountUpNumber({ target, duration = 1500, color }) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (target === 0) return;
    const startTime = Date.now();
    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCurrent(Math.round(eased * target * 10) / 10);
      if (progress >= 1) clearInterval(timer);
    }, 30);
    return () => clearInterval(timer);
  }, [target, duration]);

  return (
    <span className="text-6xl sm:text-7xl font-black tabular-nums" style={{ color }}>
      {Math.round(current * 10) / 10}
    </span>
  );
}

export default function ScoreCelebration({
  firstName = '',
  scoreBefore = 0,
  scoreAfter = 0,
  tipsCount = 0,
  onSeeTips,
  onClose,
}) {
  const { language } = useLanguage();
  const t = translations[language] || translations.fr;
  const delta = scoreAfter - scoreBefore;
  const showGlowUp = delta >= 4;
  const [visible, setVisible] = useState(false);
  const [generating, setGenerating] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    // Trigger entrance animation
    requestAnimationFrame(() => setVisible(true));
    trackEvent('celebration_shown', {
      score_before: scoreBefore,
      score_after: scoreAfter,
      tips_count: tipsCount,
    });
  }, []);

  const generateCardImage = useCallback(async () => {
    setGenerating(true);
    try {
      const W = 600, H = 700;
      const canvas = document.createElement('canvas');
      canvas.width = W * 2;
      canvas.height = H * 2;
      const ctx = canvas.getContext('2d');
      ctx.scale(2, 2);

      // --- Background gradient ---
      const bgGrad = ctx.createLinearGradient(0, 0, W, H);
      bgGrad.addColorStop(0, '#0F172A');
      bgGrad.addColorStop(0.5, '#1E293B');
      bgGrad.addColorStop(1, '#0F172A');
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, W, H);

      // --- Decorative glow circles ---
      ctx.globalAlpha = 0.08;
      ctx.fillStyle = '#5BA946';
      ctx.beginPath(); ctx.arc(100, 150, 200, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#FEA501';
      ctx.beginPath(); ctx.arc(500, 500, 180, 0, Math.PI * 2); ctx.fill();
      ctx.globalAlpha = 1;

      // --- Top accent line ---
      const accentGrad = ctx.createLinearGradient(100, 0, W - 100, 0);
      accentGrad.addColorStop(0, '#5BA946');
      accentGrad.addColorStop(0.5, '#3DC73E');
      accentGrad.addColorStop(1, '#FEA501');
      ctx.fillStyle = accentGrad;
      ctx.beginPath();
      ctx.roundRect(W / 2 - 150, 30, 300, 4, 2);
      ctx.fill();

      // --- Logo ---
      try {
        const logo = new Image();
        logo.crossOrigin = 'anonymous';
        logo.src = '/favicon-edomatch.png';
        await new Promise((resolve, reject) => { logo.onload = resolve; logo.onerror = reject; setTimeout(reject, 2000); });
        ctx.drawImage(logo, W / 2 - 22, 55, 44, 44);
      } catch {}

      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 16px -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('EDOMATCH', W / 2, 120);

      // --- Name ---
      let y = 155;
      if (firstName) {
        ctx.fillStyle = '#94A3B8';
        ctx.font = '15px -apple-system, sans-serif';
        ctx.fillText(firstName, W / 2, y);
        y += 30;
      }

      // --- Heading ---
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 26px -apple-system, sans-serif';
      ctx.fillText(t.yourScore, W / 2, y + 10);
      y += 50;

      // --- Glow-up badge ---
      if (showGlowUp) {
        const grad = ctx.createLinearGradient(W / 2 - 90, y, W / 2 + 90, y);
        grad.addColorStop(0, '#FEA501');
        grad.addColorStop(1, '#3DC73E');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.roundRect(W / 2 - 90, y - 12, 180, 28, 14);
        ctx.fill();
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 12px -apple-system, sans-serif';
        ctx.fillText(t.glowUp, W / 2, y + 6);
        y += 45;
      }

      function getColor(s) {
        if (s >= 9) return '#FFD700';
        if (s >= 7) return '#3DC73E';
        if (s >= 5) return '#FEA501';
        return '#E53E3E';
      }

      // --- Score cards background ---
      const cardY = y;
      // Before card
      ctx.fillStyle = 'rgba(255,255,255,0.05)';
      ctx.beginPath(); ctx.roundRect(W / 2 - 200, cardY - 10, 170, 140, 16); ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.1)';
      ctx.lineWidth = 1;
      ctx.stroke();
      // After card
      ctx.fillStyle = 'rgba(255,255,255,0.05)';
      ctx.beginPath(); ctx.roundRect(W / 2 + 30, cardY - 10, 170, 140, 16); ctx.fill();
      ctx.strokeStyle = 'rgba(91,169,70,0.3)';
      ctx.stroke();

      // Labels
      ctx.fillStyle = '#64748B';
      ctx.font = '11px -apple-system, sans-serif';
      ctx.fillText(t.before.toUpperCase(), W / 2 - 115, cardY + 18);
      ctx.fillText(t.after.toUpperCase(), W / 2 + 115, cardY + 18);

      // Before score
      ctx.font = 'bold 64px -apple-system, sans-serif';
      ctx.fillStyle = getColor(scoreBefore);
      ctx.fillText(String(scoreBefore), W / 2 - 130, cardY + 90);
      ctx.fillStyle = '#475569';
      ctx.font = '22px -apple-system, sans-serif';
      ctx.fillText('/10', W / 2 - 80, cardY + 90);

      // Arrow between cards
      const arrowGrad = ctx.createLinearGradient(W / 2 - 20, cardY + 60, W / 2 + 20, cardY + 60);
      arrowGrad.addColorStop(0, '#FEA501');
      arrowGrad.addColorStop(1, '#3DC73E');
      ctx.fillStyle = arrowGrad;
      ctx.font = 'bold 28px -apple-system, sans-serif';
      ctx.fillText('→', W / 2, cardY + 75);

      // After score
      ctx.font = 'bold 64px -apple-system, sans-serif';
      ctx.fillStyle = getColor(scoreAfter);
      ctx.fillText(String(scoreAfter), W / 2 + 100, cardY + 90);
      ctx.fillStyle = '#475569';
      ctx.font = '22px -apple-system, sans-serif';
      ctx.fillText('/10', W / 2 + 150, cardY + 90);

      y = cardY + 165;

      // --- Tips count ---
      if (tipsCount > 0) {
        ctx.fillStyle = '#5BA946';
        ctx.font = 'bold 14px -apple-system, sans-serif';
        ctx.fillText(`✅ ${tipsCount} ${tipsCount === 1 ? t.tipIdentified : t.tipsIdentified}`, W / 2, y);
        y += 40;
      }

      // --- Progress bar ---
      const barW = 300, barH = 8, barX = W / 2 - barW / 2;
      ctx.fillStyle = 'rgba(255,255,255,0.1)';
      ctx.beginPath(); ctx.roundRect(barX, y, barW, barH, 4); ctx.fill();
      const fillW = (scoreAfter / 10) * barW;
      const barGrad = ctx.createLinearGradient(barX, y, barX + fillW, y);
      barGrad.addColorStop(0, '#5BA946');
      barGrad.addColorStop(1, '#3DC73E');
      ctx.fillStyle = barGrad;
      ctx.beginPath(); ctx.roundRect(barX, y, fillW, barH, 4); ctx.fill();
      y += 35;

      // --- CTA box ---
      ctx.fillStyle = 'rgba(91,169,70,0.15)';
      ctx.beginPath(); ctx.roundRect(W / 2 - 170, y - 5, 340, 36, 18); ctx.fill();
      ctx.strokeStyle = 'rgba(91,169,70,0.3)';
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = '#5BA946';
      ctx.font = 'bold 13px -apple-system, sans-serif';
      const ctaText = language === 'fr' ? '💡 Vérifie ton score → cv.edomatch.com' : '💡 Check your score → cv.edomatch.com';
      ctx.fillText(ctaText, W / 2, y + 18);

      // --- Bottom accent line ---
      ctx.fillStyle = accentGrad;
      ctx.beginPath();
      ctx.roundRect(W / 2 - 100, H - 25, 200, 3, 2);
      ctx.fill();

      setGenerating(false);
      return canvas.toDataURL('image/png');
    } catch (err) {
      console.error('Card generation error:', err);
      setGenerating(false);
      return null;
    }
  }, [scoreBefore, scoreAfter, tipsCount, firstName, language, t, showGlowUp]);

  const isMobile = /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  const handleWhatsApp = useCallback(async () => {
    const dataUrl = await generateCardImage();
    
    if (isMobile && dataUrl && navigator.share && navigator.canShare) {
      // MOBILE: Use native share sheet → sends image + text to WhatsApp
      try {
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        const file = new File([blob], 'edomatch-score.png', { type: 'image/png' });
        const shareData = { text: getShareText(scoreBefore, scoreAfter, language), files: [file] };
        if (navigator.canShare(shareData)) {
          await navigator.share(shareData);
          trackEvent('score_shared', { channel: 'whatsapp_mobile_image', score_before: scoreBefore, score_after: scoreAfter });
          return;
        }
      } catch (err) {
        if (err.name !== 'AbortError') console.log('Native share failed:', err);
      }
    }
    
    // DESKTOP: Open WhatsApp Web with text, then auto-download the card image
    const text = encodeURIComponent(getShareText(scoreBefore, scoreAfter, language));
    window.open(`https://wa.me/?text=${text}`, '_blank');
    // Download image after a small delay so WhatsApp opens first
    if (dataUrl) {
      setTimeout(() => {
        const link = document.createElement('a');
        link.download = 'edomatch-score.png';
        link.href = dataUrl;
        link.click();
      }, 500);
    }
    trackEvent('score_shared', { channel: isMobile ? 'whatsapp_mobile' : 'whatsapp_desktop', score_before: scoreBefore, score_after: scoreAfter });
  }, [scoreBefore, scoreAfter, language, generateCardImage, isMobile]);

  const handleSeeTips = useCallback(() => {
    trackEvent('celebration_see_tips', { score_before: scoreBefore, score_after: scoreAfter });
    onSeeTips?.();
  }, [scoreBefore, scoreAfter, onSeeTips]);

  return (
    <>
      <style>{`
        @keyframes confettiFall {
          0% { opacity: 1; transform: translateY(0) rotate(0deg); }
          100% { opacity: 0; transform: translateY(100vh) rotate(720deg); }
        }
        @keyframes celebrationPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes arrowBounce {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(8px); }
        }
      `}</style>

      <div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        style={{
          backgroundColor: 'rgba(0,0,0,0.85)',
          backdropFilter: 'blur(8px)',
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.4s ease',
        }}
        onClick={(e) => e.target === e.currentTarget && onClose?.()}
      >
        {/* Confetti - ignored during image capture */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-ignore-capture="true">
          {Array.from({ length: 50 }).map((_, i) => (
            <ConfettiPiece key={i} index={i} />
          ))}
        </div>

        {/* Card */}
        <div
          ref={cardRef}
          className="relative w-full max-w-md rounded-3xl overflow-hidden shadow-2xl"
          style={{
            background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)',
            animation: visible ? 'slideUp 0.6s ease forwards' : 'none',
          }}
        >
          {/* Decorative glows */}
          <div className="absolute top-0 left-0 w-48 h-48 rounded-full opacity-[0.06]" style={{ background: '#5BA946', filter: 'blur(60px)' }} />
          <div className="absolute bottom-0 right-0 w-40 h-40 rounded-full opacity-[0.06]" style={{ background: '#FEA501', filter: 'blur(60px)' }} />
          {/* Top accent line */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 w-40 h-1 rounded-full bg-gradient-to-r from-[#5BA946] via-[#3DC73E] to-[#FEA501]" />

          <div className="relative z-10 p-6 sm:p-8 flex flex-col items-center text-center">
            {/* Logo */}
            <img
              src="/favicon-edomatch.png"
              alt="EdoMatch"
              className="w-12 h-12 mb-1.5"
            />
            <span className="text-white font-bold text-sm tracking-[0.2em] mb-4 opacity-60">EDOMATCH</span>

            {/* Name */}
            {firstName && (
              <p className="text-gray-300 text-base mb-2">{firstName}</p>
            )}

            {/* Heading */}
            <h2 className="text-white text-2xl sm:text-3xl font-bold mb-6">{t.yourScore}</h2>

            {/* Glow-up badge */}
            {showGlowUp && (
              <div
                className="bg-gradient-to-r from-[#FEA501] to-[#3DC73E] text-white text-sm font-bold px-5 py-2 rounded-full mb-6"
                style={{ animation: 'celebrationPulse 2s ease-in-out infinite' }}
              >
                {t.glowUp}
              </div>
            )}

            {/* Scores */}
            <div className="flex items-center gap-3 mb-6">
              {/* Before card */}
              <div className="bg-white/5 border border-white/10 rounded-xl px-5 py-3 text-center min-w-[100px]">
                <p className="text-slate-500 text-[10px] uppercase tracking-widest mb-1">{t.before}</p>
                <CountUpNumber target={scoreBefore} duration={1000} color={getScoreColor(scoreBefore)} />
                <span className="text-slate-500 text-lg">/10</span>
              </div>

              {/* Gradient arrow */}
              <div
                className="text-2xl font-bold bg-gradient-to-r from-[#FEA501] to-[#3DC73E] bg-clip-text text-transparent"
                style={{ animation: 'arrowBounce 1.5s ease-in-out infinite' }}
              >
                →
              </div>

              {/* After card */}
              <div className="bg-white/5 border border-[#5BA946]/30 rounded-xl px-5 py-3 text-center min-w-[100px]">
                <p className="text-slate-500 text-[10px] uppercase tracking-widest mb-1">{t.after}</p>
                <CountUpNumber target={scoreAfter} duration={2000} color={getScoreColor(scoreAfter)} />
                <span className="text-slate-500 text-lg">/10</span>
              </div>
            </div>

            {/* Progress bar */}
            <div className="w-64 h-1.5 bg-white/10 rounded-full mb-4 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-[#5BA946] to-[#3DC73E]" style={{ width: `${(scoreAfter / 10) * 100}%` }} />
            </div>

            {/* Tips count */}
            {tipsCount > 0 && (
              <p className="text-gray-300 text-sm mb-6">
                ✅ {tipsCount} {tipsCount === 1 ? t.tipIdentified : t.tipsIdentified}
              </p>
            )}

            {/* CTA pill */}
            <div className="bg-[#5BA946]/15 border border-[#5BA946]/30 rounded-full px-5 py-2 mb-4">
              <p className="text-[#5BA946] text-xs font-semibold">
                💡 {language === 'fr' ? 'Vérifie ton score → cv.edomatch.com' : 'Check your score → cv.edomatch.com'}
              </p>
            </div>

            {/* Share buttons */}
            <div className="flex gap-3 mb-6 w-full" data-ignore-capture="true">
              <Button
                onClick={handleWhatsApp}
                disabled={generating}
                className="flex-1 bg-[#25D366] hover:bg-[#20bd5a] text-white gap-2"
                size="sm"
              >
                <MessageCircle className="w-4 h-4" />
                {generating ? '...' : t.whatsapp}
              </Button>
              <Button
                onClick={async () => {
                  const dataUrl = await generateCardImage();
                  if (!dataUrl) return;
                  // Try native share with image
                  if (navigator.share && navigator.canShare) {
                    try {
                      const res = await fetch(dataUrl);
                      const blob = await res.blob();
                      const file = new File([blob], 'edomatch-score.png', { type: 'image/png' });
                      const shareData = { text: getShareText(scoreBefore, scoreAfter, language), files: [file] };
                      if (navigator.canShare(shareData)) {
                        await navigator.share(shareData);
                        trackEvent('score_shared', { channel: 'native_celebration_image' });
                        return;
                      }
                    } catch {}
                  }
                  // Fallback: just download
                  const link = document.createElement('a');
                  link.download = 'edomatch-score.png';
                  link.href = dataUrl;
                  link.click();
                  trackEvent('score_shared', { channel: 'download_celebration' });
                }}
                disabled={generating}
                variant="outline"
                className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800 gap-2"
                size="sm"
              >
                <Share2 className="w-4 h-4" />
                {generating ? '...' : t.shareScore}
              </Button>
            </div>

            {/* Primary CTA */}
            <Button
              onClick={handleSeeTips}
              data-ignore-capture="true"
              className="w-full bg-[#3DC73E] hover:bg-[#35b336] text-white py-6 text-lg font-bold rounded-xl"
            >
              {t.seeTips}
            </Button>
          </div>
          {/* Bottom accent line */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 w-28 h-0.5 rounded-full bg-gradient-to-r from-[#5BA946] via-[#3DC73E] to-[#FEA501] z-10" />
        </div>
      </div>
    </>
  );
}
